import BaseModule from '../_base_module';

/**
 * The member's notification category switches (PREF-01, PREF-02, PREF-03).
 *
 * This is ACCOUNT state, which is why it is a module of its own rather than a few extra
 * keys on `push`. `push` is DEVICE state -- token, OS permission, registration -- and the
 * two have opposite logout semantics: the push module must survive a logout (Phase 39-11's
 * "has this device been asked yet" flag depends on it), and this one must not.
 *
 * NOTE ON PERSISTENCE. vuex-persist is configured with no reducer (src/store/index.js), so
 * it mirrors the WHOLE store -- including this module -- into localStorage under
 * `private-gym`. On a shared phone that means the next member to log in would render the
 * previous member's toggles out of cache before the fetch returns. `current_user/logout`
 * therefore commits this module's `clear`, and `clear` is overridden below to reset all
 * four keys rather than only `data`.
 *
 * PREF-03, client half: "no stored preference" is NEVER read here as "off". The server
 * resolves every category against its default and sends the answer; this module renders
 * what it is given and re-derives nothing. That is the whole reason the API returns all
 * five categories with `enabled` already resolved -- so an old client that stays installed
 * for months cannot get the defaults wrong.
 *
 * The Dutch labels and descriptions are server-owned. There is deliberately no copy of them
 * in this file: one source of truth is `NotificationCategory::label()` in the API. A client
 * mirror is exactly the drift that left `marketing` missing from src/js/push.js.
 */

/**
 * Group order for the settings screen (IC2).
 *
 * These are `legal_basis` values, not categories. The client must never hold a
 * `category -> group` map: grouping comes from the `legal_basis` the server attached to
 * each entry, so a category this client predates is GROUPED rather than dropped. Anything
 * outside this list is appended after it, sorted, and the page titles it "Overig".
 */
const GROUP_ORDER = ['contract', 'consent'];

/**
 * State as a factory, not a shared object.
 *
 * The older modules in this store export a plain object, which means every Vuex store
 * created in the same process shares one state instance -- fine in the app, where there is
 * exactly one store, but it leaks between tests. This module opts into the isolated form,
 * like `push`, so its tests do not depend on ordering.
 */
const state = () => ({
  ...BaseModule.state,

  // The enriched category list exactly as the server sent it, in server order.
  data: [],

  // A fetch has completed -- successfully or not. Distinct from `load_failed` so the
  // screen can tell "still loading" (preloader) from "loaded and empty-because-broken".
  loaded: false,

  // The last fetch failed, or came back malformed. The screen renders its S9 failure
  // block; deliberately no toast, since S9 is the only surface for a load failure.
  load_failed: false,

  // The category whose PUT is in flight, or null. One category at a time: only that row's
  // toggle is disabled while it saves -- never the list, never the page, never the global
  // loader (IC1).
  saving: null,
});

const getters = {
  ...BaseModule.getters,

  all(state) {
    return state.data;
  },

  saving(state) {
    return state.saving;
  },

  has_loaded(state) {
    return state.loaded;
  },

  load_failed(state) {
    return state.load_failed;
  },

  /**
   * The IC2 grouping: [{ legal_basis, items: [...] }, ...].
   *
   * Returns `legal_basis` keys only -- no Dutch titles. The page owns the
   * legal_basis -> title/footnote map, because that is copy and this is state.
   *
   * `contract` first, `consent` second, anything the client has never seen appended after
   * them in sorted order so the result is deterministic. A group with no categories in it
   * cannot occur: buckets only exist because something was put in them, so there is no
   * empty title, no empty list and no orphan footnote to render.
   */
  groups(state) {
    const buckets = new Map();

    state.data.forEach((item) => {
      const basis = item.legal_basis;

      if (!buckets.has(basis)) {
        buckets.set(basis, []);
      }

      buckets.get(basis).push(item);
    });

    const known = GROUP_ORDER.filter((basis) => buckets.has(basis));
    const unknown = [...buckets.keys()]
      .filter((basis) => !GROUP_ORDER.includes(basis))
      .sort();

    return [...known, ...unknown].map((legal_basis) => ({
      legal_basis,
      items: buckets.get(legal_basis),
    }));
  },
};

const actions = {
  ...BaseModule.actions,

  /**
   * Load every category for the current member (PREF-01).
   *
   * Resolves rather than rejects on failure. The caller is a page's `mounted()`, the screen
   * renders its own failure block off `load_failed`, and there is nothing further up to
   * rethrow to -- an unhandled rejection here would only be a console error nobody reads.
   */
  fetch_all({commit, dispatch}) {
    commit('set_state', {property: 'load_failed', value: false});

    return dispatch('api/getEndpoint', {
      endpoint: '/notification-preferences',
      // The screen's own failure block (S9) is the only surface for a load failure, so the
      // global danger toast from api/onError would be a second, redundant message.
      suppress_error_toast: true,
    }, {root: true})
      .then((response) => {
        const categories = response?.data?.data;

        // A response with zero categories is a MALFORMED response, not an empty state:
        // the server always returns all five. Treating it as "no preferences" would render
        // a blank settings screen and quietly invite the member to conclude there is
        // nothing to configure.
        if (!Array.isArray(categories) || categories.length === 0) {
          commit('set_state', {property: 'data', value: []});
          commit('set_state', {property: 'load_failed', value: true});

          return null;
        }

        commit('set_state', {property: 'data', value: categories});
        commit('set_state', {property: 'load_failed', value: false});

        return categories;
      })
      .catch(() => {
        commit('set_state', {property: 'data', value: []});
        commit('set_state', {property: 'load_failed', value: true});

        return null;
      })
      .finally(() => {
        commit('set_state', {property: 'loaded', value: true});
      });
  },

  /**
   * Write one category, optimistically (IC1, PREF-01).
   *
   * There is no save button on this screen: the toggle flips immediately and the PUT
   * happens behind it. That trade buys instant feedback and costs a rollback path, which is
   * the whole of the complexity below.
   *
   * Resolves `true` on success and `false` on failure, and NEVER throws. The caller is a
   * toggle's change handler; an unhandled rejection in a Vue event handler is a console
   * error nobody sees plus a toggle left stuck in the wrong position.
   *
   * @param {{category: string, enabled: boolean}} payload
   * @returns {Promise<boolean>}
   */
  async set_category({state, commit, dispatch}, {category, enabled}) {
    // The WHOLE previous list, copied, rather than just the one boolean. Rollback then
    // restores exactly what was on screen instead of re-deriving it -- and re-deriving is
    // the trap here, because "what it was before" for a category the client predates is
    // not something the client can reconstruct.
    const previous = state.data.map((item) => ({...item}));

    commit('set_state', {
      property: 'data',
      value: state.data.map((item) => (
        item.category === category ? {...item, enabled} : item
      )),
    });
    commit('set_state', {property: 'saving', value: category});

    try {
      const response = await dispatch('api/putEndpoint', {
        endpoint: '/notification-preferences',
        // Exactly ONE category per request. A full replacement from an old client would
        // wipe every category that client predates.
        data: {preferences: {[category]: enabled}},
        // Suppress the global toast: the rollback below emits the category-specific
        // message instead, and the UI contract allows exactly one, never both.
        suppress_error_toast: true,
      }, {root: true});

      const categories = response?.data?.data;

      // The server is authoritative (D8). Its echo replaces local state wholesale, so a
      // category added server-side appears without a reload and a value the server refused
      // to change is shown as it actually is, not as the optimistic flip assumed.
      // Guarded only against a malformed body: replacing a rendered list with `undefined`
      // would blank the screen on what was, by status code, a successful save.
      if (Array.isArray(categories) && categories.length > 0) {
        commit('set_state', {property: 'data', value: categories});
      }

      return true;
    } catch (error) {
      commit('set_state', {property: 'data', value: previous});

      // The label is read out of the captured previous list -- never re-fetched, never
      // hardcoded. The client holds no copy of the Dutch labels.
      const failed = previous.find((item) => item.category === category);

      dispatch('notifications/addDangerNotification', {
        message: failed?.label
          ? `${failed.label} kon niet worden opgeslagen. Probeer het opnieuw.`
          // Unreachable by construction -- the only caller is a row rendered from this very
          // list. Present because "exactly one message" also rules out zero, and
          // `undefined kon niet worden opgeslagen` is worse than losing the label.
          : 'Je keuze kon niet worden opgeslagen. Probeer het opnieuw.',
      }, {root: true});

      return false;
    } finally {
      commit('set_state', {property: 'saving', value: null});
    }
  },
};

const mutations = {
  ...BaseModule.mutations,

  /**
   * Override of BaseModule.mutations.clear. NOT redundant -- do not delete it.
   *
   * The base version resets `state.data` alone. This module has four keys, and the other
   * three are exactly the ones that would leak: a logout leaving `loaded: true` behind
   * makes the next member's settings screen skip its preloader and render the stale
   * `data`, and a stale `saving` leaves one row's toggle disabled for somebody who never
   * touched it.
   */
  clear(state) {
    state.data = [];
    state.loaded = false;
    state.load_failed = false;
    state.saving = null;
  },
};

export default {

  namespaced: true,

  state,
  getters,
  actions,
  mutations,
};
